<script setup lang="ts">
import { HomeButton } from '@/components/home'
import ShopImage from '@/assets/images/icons/shop-icon.webp'
import { twMerge } from 'tailwind-merge'
import { useShop } from '@/composables/game/shop'
import DefaultBadge from '@/components/periodic/DefaultBadge.vue'
import type { BadgeVariant } from '@/components/periodic/DefaultBadge.vue'
import { Vue3Lottie } from 'vue3-lottie'
import IWDShopJson from '@/assets/lotties/periodic/IWD/IWD-shop.json'
import STPShopJson from '@/assets/lotties/periodic/STP/STP-shop.json'
import { computed } from 'vue'

const {
  isDefaultBOGO,
  isDefaultBOGX,
  isIWD,
  isSTP,
  isDefaultCashback,
  isSale,
  isDefaultDiscount,
  countdownForSaleEnds,
  canHaveSale,
} = useShop()

defineProps<{
	hasUnseenLimitedPacks?: boolean
}>()

const saleVariant = computed<BadgeVariant>(() => {
  if (isDefaultBOGO.value) return 'BOGO'
  if (isDefaultBOGX.value) return 'BOGX'
  if (isDefaultDiscount.value) return 'discount'
  if (isDefaultCashback.value) return 'cashback'
  if (isIWD.value) return 'IWD'
  if (isSTP.value) return 'STP'
  return 'discount' // fallback
})

const buttonClass = computed(() => {
  return twMerge(
    'shop-button pulse-shadow bg-[#D2FFFB] px-[7px] pt-1.5 pb-2',
    canHaveSale.value && isDefaultDiscount.value && 'pulse-shadow-default-discount',
    isDefaultBOGO.value && 'pulse-shadow-default-BOGO',
    isDefaultCashback.value && 'pulse-shadow-default-cashback',
    isIWD.value && 'IWD-background pulse-shadow-IWD',
    isSTP.value && 'STP-background pulse-shadow-STP',
  )
})

const showSaleBadge = computed(() => canHaveSale.value && isSale.value)
</script>

<template>
  <HomeButton
    label="Shop"
    :countdown="showSaleBadge ? { value: countdownForSaleEnds, dangerThreshold: 3600 * 12 } : undefined"
    animation="scale-up"
    :button-class="buttonClass"
  >
    <template #badge-top-left>
      <div
        v-if="hasUnseenLimitedPacks"
        class="absolute top-0.5 -right-0.5 size-2.5 rounded-full bg-red-accent z-[4] pulse-dot"
      />
      <div
        v-if="showSaleBadge"
        class="absolute -left-2.5 -top-2.5 size-[38px] z-[3]"
      >
        <DefaultBadge :variant="saleVariant" />
      </div>
    </template>
    <div class="w-full h-full relative flex justify-center z-[2]">
      <Vue3Lottie
        v-if="canHaveSale && isIWD"
        :animation-data="IWDShopJson"
        :auto-play="true"
        :loop="true"
        width="100%"
        height="auto"
        class="min-w-full"
      />
      <Vue3Lottie
        v-else-if="canHaveSale && isSTP"
        :animation-data="STPShopJson"
        :auto-play="true"
        :loop="true"
        width="100%"
        height="auto"
        class="min-w-full"
      />
      <img
        v-else
        class="w-full h-full object-cover"
        :src="ShopImage"
      >
    </div>
  </HomeButton>
</template>

<style lang="scss">
@import '@/assets/styles/sales.scss';

@keyframes pulse-dot {
	0%, 100% {
		transform: scale(1);
		opacity: 1;
	}
	50% {
		transform: scale(1.35);
		opacity: 0.75;
	}
}

.pulse-dot {
	animation: pulse-dot 1.5s ease-in-out infinite;
	will-change: transform, opacity;
}

</style>
